import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { reduxForm } from 'redux-form';

import {
  MotifButton,
  MotifDropdown,
  MotifDropdownItem,
  MotifFormField,
  MotifIcon,
  MotifIconButton,
  MotifLabel,
  MotifOption,
  MotifSelect,
  MotifTooltip,
} from '@ey-xd/motif-react';
import { IconoirNavArrowDown, IconoirInfoCircle } from '@ey-xd/motif-icon';
import { addAttribute } from '../../common/Attribute';

const ManageExternalChecklistHeader = ({
  clients,
  selectedClient,
  handleClientChange,
  calendarYears,
  handleCalendarYearChange,
  selectedChecklists,
  toggleShowDownloadChecklistsModal,
  toggleShowRequestToReopenModal,
  toggleShowSubmitForEYReviewModal,
  toggleShowClientSignoffModal,
  toggleShowClientUnsignoffModal,
}) => {
  const [actionOptionsOpen, setActionOptionsOpen] = useState(false);
  useEffect(() => {
    //Add Attribute(type=button) to all button elements having attribute(role = 'combobox')
    addAttribute("button[role = 'combobox']", 'type', 'button');
  });

  let calendarYearsOptions = [];
  let disableCalendarYearDropdown = false;
  let preselectedCalendarYear = '';

  const clientsOptions = clients.map((c) => {
    return { key: c.id, value: c.name, text: c.name };
  });

  if (calendarYears.length > 0) {
    calendarYearsOptions = calendarYears.map((cy) => {
      return { key: cy, value: cy.toString(), text: cy.toString() };
    });

    preselectedCalendarYear = calendarYears.includes(new Date().getFullYear())
      ? parseInt(new Date().getFullYear()).toString()
      : calendarYears[0].toString();
  }

  // Section for download checklists
  let downloadChecklistsActionStatus = true;
  let downloadChecklistsToolTipMessage = '';
  let submitForReviewStatus = true;
  let submitForReviewToolTipMessage = '';
  const checklistStatusForReview = [
    'Created',
    'Sent',
    'InProgress',
    'PENDINGINTERNALREVIEW',
  ];
  if (selectedChecklists && selectedChecklists.length >= 1) {
    if (selectedChecklists.length > 100) {
      downloadChecklistsActionStatus = false;
      downloadChecklistsToolTipMessage =
        'Please select 100 or less items to download.';
    }
    if (
      selectedChecklists.find(
        (f) => !checklistStatusForReview.includes(f.checklistState)
      )
    ) {
      submitForReviewStatus = false;
      submitForReviewToolTipMessage =
        'Please make sure all selected checklists should be either in Created, Sent, In Progress, or Pending Internal Review state.';
    } else if (
      selectedChecklists.find(
        (c) => !c.canExternalUserSubmitChecklistForEYReview
      )
    ) {
      submitForReviewStatus = false;
      submitForReviewToolTipMessage =
        'Please make sure to complete the checklist in order to submit for review.';
    }
  } else {
    downloadChecklistsActionStatus = false;
    downloadChecklistsToolTipMessage =
      'Please select at least 1 checklist to download.';
    submitForReviewStatus = false;
    submitForReviewToolTipMessage =
      'Please select at least 1 checklist for EY review';
  }

  let signoffActionStatus = true;
  let signoffToolTipMessage = '';
  if (!selectedChecklists || !selectedChecklists.length > 0) {
    signoffActionStatus = false;
    signoffToolTipMessage = 'Please select at least 1 checklist to sign off.';
  } else if (selectedChecklists && selectedChecklists.length > 0) {
    // Check if there is any selected checklist is already signed off
    if (selectedChecklists.some((record) => record.isSignedOff)) {
      signoffActionStatus = false;
      signoffToolTipMessage =
        'Selected checklist(s) is signed off by the client.';
    }
  }
  let unsignoffActionStatus = true;
  let unsignoffToolTipMessage = '';
  if (!selectedChecklists || !selectedChecklists.length > 0) {
    unsignoffActionStatus = false;
    unsignoffToolTipMessage =
      'Please select at least 1 checklist to unsign off.';
  } else if (selectedChecklists && selectedChecklists.length > 0) {
    // Check if there is any selected checklist is not signed off
    if (selectedChecklists.some((record) => !record.isSignedOff)) {
      unsignoffActionStatus = false;
      unsignoffToolTipMessage =
        'Selected checklist(s) is not signed off by the client.';
    }
  }

  const checklistStatusList = ['completed', 'pendingeyreview'];
  let requestToReopenActionStatus = true;
  let requestToReopenToolTipMessage = '';

  if (!selectedChecklists || !selectedChecklists.length > 0) {
    requestToReopenActionStatus = false;
    requestToReopenToolTipMessage =
      'Please select at least 1 checklist to request to reopen.';
  } else if (selectedChecklists && selectedChecklists.length > 0) {
    // Check if there is any selected checklist status not in completed or pending ey review state
    if (
      selectedChecklists.some(
        (record) =>
          !checklistStatusList.includes(record.checklistState.toLowerCase())
      )
    ) {
      requestToReopenActionStatus = false;
      requestToReopenToolTipMessage =
        'Please make sure all selected checklists should be either in Completed or Pending EY Review state.';
    }
  }

  return (
    <div>
      <div style={{ minHeight: '24px' }}></div>
      <form>
        <div className="row rowheight">
          <div className="col-md-2 pl-3">
            <MotifFormField>
              <MotifLabel
                data-testid="lblClientName"
                position="in"
                className="motif-label-with-content"
              >
                Client Name
              </MotifLabel>
              <MotifSelect
                className="motif-select-extChecklist motif-select-hide-clear"
                name="clients"
                value={
                  clientsOptions.length > 1 ? null : clientsOptions[0].value
                }
                onChange={handleClientChange}
                data-testid="divClientName"
                labelPosition="in"
              >
                {clientsOptions.map((co) => (
                  <MotifOption
                    data-testvalue={co.text}
                    key={co.key}
                    value={co.value}
                  >
                    {co.text}
                  </MotifOption>
                ))}
              </MotifSelect>
            </MotifFormField>
          </div>
          {calendarYearsOptions.length > 0 && (
            <React.Fragment>
              <div className="col-md-2 pl-0">
                <MotifFormField>
                  <MotifLabel data-testid="lblCalenderYear" position="in">
                    Calendar Year
                  </MotifLabel>
                  <MotifSelect
                    className="motif-select-extChecklist motif-select-hide-clear"
                    name="calendarYears"
                    value={preselectedCalendarYear}
                    onChange={handleCalendarYearChange}
                    data-testid="divCalendarYear"
                    disabled={disableCalendarYearDropdown}
                    labelPosition="in"
                  >
                    {calendarYearsOptions.map((cy) => (
                      <MotifOption
                        data-testvalue={cy.text}
                        key={cy.key}
                        value={cy.value}
                      >
                        {cy.value}
                      </MotifOption>
                    ))}
                  </MotifSelect>
                </MotifFormField>
              </div>
              <div className="col-md-6 pl-0"></div>
              <div
                className="col-md-2 d-flex justify-content-end"
                style={{ width: 'fit-content' }}
              >
                <MotifDropdown
                  open={actionOptionsOpen}
                  placement="bottom-end"
                  id={'action-dropdown'}
                  className="checklist-dropdown-action"
                  aria-labelledby={'manage-external-checklist-trigger'}
                  handleClickOutside={() => setActionOptionsOpen(false)}
                  trigger={
                    <MotifButton
                      type="button"
                      variant="primary-alt"
                      onClick={() => setActionOptionsOpen(!actionOptionsOpen)}
                      aria-controls={'action-dropdown'}
                      id={'action-dropdown-trigger'}
                      aria-expanded={actionOptionsOpen}
                      aria-label="Action dropdown"
                      data-testid="actions"
                    >
                      Actions{' '}
                      <MotifIcon iconFunction={IconoirNavArrowDown} size="24" />
                    </MotifButton>
                  }
                >
                  <ul role="menu">
                    {(submitForReviewStatus && (
                      <li role="menuitem">
                        <MotifDropdownItem
                          onClick={() => {
                            setActionOptionsOpen(false);
                            toggleShowSubmitForEYReviewModal();
                          }}
                          type="button"
                          disabled={!submitForReviewStatus}
                          data-testid="submitForEYReview"
                        >
                          Submit for EY Review
                        </MotifDropdownItem>
                      </li>
                    )) || (
                      <li role="menuitem">
                        <MotifTooltip
                          placement="right"
                          trigger={
                            <MotifDropdownItem
                              onclick={() => {
                                setActionOptionsOpen(false);
                                toggleShowSubmitForEYReviewModal();
                              }}
                              type="button"
                              disabled={!submitForReviewStatus}
                              data-testid="submitForEYReview"
                            >
                              Submit for EY Review
                              <MotifIconButton
                                className="ml-2"
                                aria-label="Submit for EY Review"
                                type="button"
                              >
                                <MotifIcon
                                  iconFunction={IconoirInfoCircle}
                                  size="24"
                                />
                              </MotifIconButton>
                            </MotifDropdownItem>
                          }
                        >
                          {submitForReviewToolTipMessage}
                        </MotifTooltip>
                      </li>
                    )}
                    {(signoffActionStatus && (
                      <li role="menuitem">
                        <MotifDropdownItem
                          onClick={() => {
                            setActionOptionsOpen(false);
                            toggleShowClientSignoffModal();
                          }}
                          type="button"
                          disabled={!signoffActionStatus}
                          data-testid="clientSignoff"
                        >
                          Client Sign Off
                        </MotifDropdownItem>
                      </li>
                    )) || (
                      <li role="menuitem">
                        <MotifTooltip
                          placement="right"
                          trigger={
                            <MotifDropdownItem
                              onclick={() => {
                                setActionOptionsOpen(false);
                                toggleShowClientSignoffModal();
                              }}
                              type="button"
                              disabled={!signoffActionStatus}
                              data-testid="clientSignoff"
                            >
                              Client Sign Off
                              <MotifIconButton
                                className="ml-2"
                                aria-label="Client Sign Off"
                                type="button"
                              >
                                <MotifIcon
                                  iconFunction={IconoirInfoCircle}
                                  size="24"
                                />
                              </MotifIconButton>
                            </MotifDropdownItem>
                          }
                        >
                          {signoffToolTipMessage}
                        </MotifTooltip>
                      </li>
                    )}

                    {(unsignoffActionStatus && (
                      <li role="menuitem">
                        <MotifDropdownItem
                          onClick={() => {
                            setActionOptionsOpen(false);
                            toggleShowClientUnsignoffModal();
                          }}
                          type="button"
                          disabled={!unsignoffActionStatus}
                          data-testid="clientUnsignoff"
                        >
                          Client Unsign Off
                        </MotifDropdownItem>
                      </li>
                    )) || (
                      <li role="menuitem">
                        <MotifTooltip
                          placement="right"
                          trigger={
                            <MotifDropdownItem
                              onclick={() => {
                                setActionOptionsOpen(false);
                                toggleShowClientUnsignoffModal();
                              }}
                              type="button"
                              disabled={!unsignoffActionStatus}
                              data-testid="clientUnsignoff"
                            >
                              Client Unsign Off
                              <MotifIconButton
                                className="ml-2"
                                aria-label="Client Unsign Off"
                                type="button"
                              >
                                <MotifIcon
                                  iconFunction={IconoirInfoCircle}
                                  size="24"
                                />
                              </MotifIconButton>
                            </MotifDropdownItem>
                          }
                        >
                          {unsignoffToolTipMessage}
                        </MotifTooltip>
                      </li>
                    )}
                    {(requestToReopenActionStatus && (
                      <li role="menuitem">
                        <MotifDropdownItem
                          onClick={() => {
                            setActionOptionsOpen(false);
                            toggleShowRequestToReopenModal();
                          }}
                          type="button"
                          disabled={!requestToReopenActionStatus}
                          data-testid="requestToReopen"
                        >
                          Request to Reopen
                        </MotifDropdownItem>
                      </li>
                    )) || (
                      <li role="menuitem">
                        <MotifTooltip
                          placement="right"
                          trigger={
                            <MotifDropdownItem
                              onClick={() => {
                                setActionOptionsOpen(false);
                                toggleShowRequestToReopenModal();
                              }}
                              type="button"
                              disabled={!requestToReopenActionStatus}
                              data-testid="requestToReopen"
                            >
                              Request to Reopen
                              <MotifIconButton
                                className="ml-2"
                                aria-label="Tooltip"
                                type="button"
                              >
                                <MotifIcon
                                  iconFunction={IconoirInfoCircle}
                                  size="24"
                                />
                              </MotifIconButton>
                            </MotifDropdownItem>
                          }
                        >
                          {requestToReopenToolTipMessage}
                        </MotifTooltip>
                      </li>
                    )}
                    {/* Dropdown item for "Download - with Answers only */}
                    {(downloadChecklistsActionStatus && (
                      <li role="menuitem">
                        <MotifDropdownItem
                          onClick={() => {
                            setActionOptionsOpen(false);
                            toggleShowDownloadChecklistsModal('WITH_ANSWERS');
                          }}
                          type="button"
                          disabled={!downloadChecklistsActionStatus}
                          data-testid="downloadWithAnswersOnly"
                        >
                          Download - with Answers Only
                        </MotifDropdownItem>
                      </li>
                    )) || (
                      <li role="menuitem">
                        <MotifTooltip
                          placement="right"
                          trigger={
                            <MotifDropdownItem
                              onclick={() => {
                                setActionOptionsOpen(false);
                                toggleShowDownloadChecklistsModal(
                                  'WITH_ANSWERS'
                                );
                              }}
                              type="button"
                              disabled={!downloadChecklistsActionStatus}
                              data-testid="downloadWithAnswersOnly"
                            >
                              Download - with Answers Only
                              <MotifIconButton
                                className="ml-2"
                                aria-label="Download - with Answers Only"
                                type="button"
                              >
                                <MotifIcon
                                  iconFunction={IconoirInfoCircle}
                                  size="24"
                                />
                              </MotifIconButton>
                            </MotifDropdownItem>
                          }
                        >
                          {downloadChecklistsToolTipMessage}
                        </MotifTooltip>
                      </li>
                    )}
                    {/* Dropdown item for "Download - with Answers and Comments" */}
                    {(downloadChecklistsActionStatus && (
                      <li role="menuitem">
                        <MotifDropdownItem
                          onClick={() => {
                            setActionOptionsOpen(false);
                            toggleShowDownloadChecklistsModal(
                              'WITH_ANSWERS_COMMENTS'
                            );
                          }}
                          type="button"
                          disabled={!downloadChecklistsActionStatus}
                          data-testid="downloadWithAnswersAndComments"
                        >
                          Download - with Answers and Comments
                        </MotifDropdownItem>
                      </li>
                    )) || (
                      <li role="menuitem">
                        <MotifTooltip
                          placement="right"
                          trigger={
                            <MotifDropdownItem
                              onclick={() => {
                                setActionOptionsOpen(false);
                                toggleShowDownloadChecklistsModal(
                                  'WITH_ANSWERS_COMMENTS'
                                );
                              }}
                              type="button"
                              disabled={!downloadChecklistsActionStatus}
                              data-testid="downloadWithAnswersAndComments"
                            >
                              Download - with Answers and Comments
                              <MotifIconButton
                                className="ml-2"
                                aria-label="Download - with Answers and Comments"
                                type="button"
                              >
                                <MotifIcon
                                  iconFunction={IconoirInfoCircle}
                                  size="24"
                                />
                              </MotifIconButton>
                            </MotifDropdownItem>
                          }
                        >
                          {downloadChecklistsToolTipMessage}
                        </MotifTooltip>
                      </li>
                    )}
                    {/* Dropdown items for "Download - without Answers or Comments" */}
                    {(downloadChecklistsActionStatus && (
                      <li role="menuitem">
                        <MotifDropdownItem
                          onClick={() => {
                            setActionOptionsOpen(false);
                            toggleShowDownloadChecklistsModal(
                              'WITHOUT_ANSWERS'
                            );
                          }}
                          type="button"
                          disabled={!downloadChecklistsActionStatus}
                          data-testid="downloadWithoutAnswersOrComments"
                        >
                          Download - without Answers or Comments
                        </MotifDropdownItem>
                      </li>
                    )) || (
                      <li role="menuitem">
                        <MotifTooltip
                          placement="right"
                          trigger={
                            <MotifDropdownItem
                              onclick={() => {
                                setActionOptionsOpen(false);
                                toggleShowDownloadChecklistsModal(
                                  'WITHOUT_ANSWERS'
                                );
                              }}
                              type="button"
                              disabled={!downloadChecklistsActionStatus}
                              data-testid="downloadWithoutAnswersOrComments"
                            >
                              Download - without Answers or Comments
                              <MotifIconButton
                                className="ml-2"
                                aria-label="Download - without Answers or Comments"
                                type="button"
                              >
                                <MotifIcon
                                  iconFunction={IconoirInfoCircle}
                                  size="24"
                                />
                              </MotifIconButton>
                            </MotifDropdownItem>
                          }
                        >
                          {downloadChecklistsToolTipMessage}
                        </MotifTooltip>
                      </li>
                    )}
                  </ul>
                </MotifDropdown>
              </div>
            </React.Fragment>
          )}
        </div>
      </form>
    </div>
  );
};

ManageExternalChecklistHeader.propTypes = {
  clients: PropTypes.array,
  selectedClient: PropTypes.object,
  handleClientChange: PropTypes.func,
  calendarYears: PropTypes.array,
  handleCalendarYearChange: PropTypes.func,
  gridCurrentState: PropTypes.any,
  selectedChecklists: PropTypes.array,
  toggleShowDownloadChecklistsModal: PropTypes.bool,
  toggleShowRequestToReopenModal: PropTypes.func,
  toggleShowSubmitForEYReviewModal: PropTypes.func,
};

export default reduxForm({ form: 'manageExternalChecklistHeader' })(
  ManageExternalChecklistHeader
);
